import { apiClient } from "@/lib/api";
import {
  parseAgreementFilter,
  type AgreementRevocationDto,
} from "@/services/admin-agreement.service";
import type {
  ExportUsersRequest,
  UserExportRow,
  User,
  UserDetail,
  UserFilters,
  UserTenant,
  UserActivity,
} from "@/types/admin";
import type {
  StandardResponse,
  MessageResponse,
  PaginatedStandardResponse,
  PaginationInfo,
} from "@/types/api-response";
import {
  isErrorResponse,
  extractData,
  extractPaginatedData,
} from "@/types/api-response";

/** Mirrors UpdateUserCommand. Email and phoneNumber are required by the backend validator. */
export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  email: string;
  phoneNumber: string;
  role?: string;
}

export interface ImpersonateResponse {
  token: string;
  redirectUrl: string;
}

export const adminUserService = {
  /**
   * Get paginated list of users with filters
   * Returns StandardResponse wrapped paginated data
   */
  async getUsers(
    filters: UserFilters,
    signal?: AbortSignal,
  ): Promise<{ data: User[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.status && filters.status !== "All") {
      // Backend expects AccountStatus enum, convert UserStatus to string
      params.append("status", filters.status);
    }
    if (filters.registeredFrom)
      params.append("registeredFrom", filters.registeredFrom);
    if (filters.registeredTo)
      params.append("registeredTo", filters.registeredTo);
    const agreement = filters.agreement
      ? parseAgreementFilter(filters.agreement)
      : undefined;
    if (agreement) {
      params.append("agreement", agreement.type);
      params.append("agreementAccepted", String(agreement.accepted));
    }
    params.append("page", filters.page.toString());
    params.append("limit", filters.pageSize.toString()); // Backend expects 'limit', we use pageSize internally

    const response = await apiClient.get<PaginatedStandardResponse<User>>(
      `/api/v1/admin/users?${params.toString()}`,
      { signal },
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Get the user rows for the Excel export
   * Returns StandardResponse with the rows; the workbook itself is built client-side
   */
  async getUsersForExport(
    payload: ExportUsersRequest,
  ): Promise<UserExportRow[]> {
    const response = await apiClient.post<StandardResponse<UserExportRow[]>>(
      "/api/v1/admin/users/export",
      payload,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Get user details by ID
   * Returns StandardResponse with user details
   */
  async getUserById(id: string): Promise<UserDetail> {
    const response = await apiClient.get<StandardResponse<UserDetail>>(
      `/api/v1/admin/users/${id}`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Update user details
   * Returns StandardResponse with updated user
   */
  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<StandardResponse<User>>(
      `/api/v1/admin/users/${id}`,
      data,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Suspend user account
   * Returns MessageResponse
   */
  async suspendUser(id: string, reason?: string): Promise<void> {
    const response = await apiClient.post<MessageResponse>(
      `/api/v1/admin/users/${id}/suspend`,
      { reason },
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },

  /**
   * Activate suspended user account
   * Returns MessageResponse
   */
  async activateUser(id: string, notes?: string): Promise<void> {
    const response = await apiClient.post<MessageResponse>(
      `/api/v1/admin/users/${id}/activate`,
      { notes: notes || null },
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },

  /**
   * Reset user password (sends reset email)
   * Returns MessageResponse
   */
  async resetPassword(id: string): Promise<void> {
    const response = await apiClient.post<MessageResponse>(
      `/api/v1/admin/users/${id}/reset-password`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
  },

  /**
   * Impersonate user (login as user)
   * Returns StandardResponse with token and redirect URL
   */
  async impersonateUser(
    id: string,
    reason: string,
  ): Promise<ImpersonateResponse> {
    const response = await apiClient.post<
      StandardResponse<ImpersonateResponse>
    >(`/api/v1/admin/users/${id}/impersonate`, { reason });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Get user's tenant memberships
   * Returns StandardResponse with array of tenant memberships
   */
  async getUserTenants(id: string): Promise<UserTenant[]> {
    const response = await apiClient.get<StandardResponse<UserTenant[]>>(
      `/api/v1/admin/users/${id}/tenants`,
    );

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },

  /**
   * Get user's activity logs
   * Returns paginated audit logs
   */
  async getUserAuditLogs(
    id: string,
    page: number = 1,
    limit: number = 20,
    actionType?: string,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<{ data: UserActivity[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (actionType) params.append("actionType", actionType);
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);

    const response = await apiClient.get<
      PaginatedStandardResponse<UserActivity>
    >(`/api/v1/admin/users/${id}/audit-logs?${params.toString()}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Revokes the user's acceptance of one commitment so they are asked for it again; with
   * includeTenants, that of every tenant they are an active member of as well.
   */
  async revokeAgreement(
    id: string,
    type: string,
    includeTenants: boolean,
  ): Promise<AgreementRevocationDto> {
    const response = await apiClient.delete<
      StandardResponse<AgreementRevocationDto>
    >(`/api/v1/admin/users/${id}/agreements/${type}`, {
      params: { includeTenants },
    });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractData(response.data);
  },
};
